import React, { useEffect, useMemo } from 'react'
import { Entries } from 'type-fest'
import { useFormContext, DeepPartial, ExtractObjects } from 'react-hook-form'
import FormFieldFromProps from '@App/kit/fields/FormFieldFromProps'
import { DiscriminatedFormFields } from '@App/forms/common/discriminated-form-fields'
import { SearchWithCriteriaFormType } from '@Components/Search/SearchWithCriteria/form-type'
import { FilterFieldGroup } from '@App/forms/common/filter-field-group'
import Button from '@Components/Buttons/Button'
import ExpandableItem from '@App/kit/components/Navigations/Sidebar/ExpandableItem'
import clsx from 'clsx'
import './index.scss'

interface FilterSectionProps<K> {
    filterCollections: Array<FilterFieldGroup<K>>
    handleFilterValueChange?: (filter: (ExtractObjects<K> extends never ? K : DeepPartial<K>) | undefined) => void
    onApplyFilter?: (value: boolean) => void
    onResetFilter?: () => void
    defaultFilterValues?: ExtractObjects<K> extends never ? K : DeepPartial<K>
}

/**
 * @template K - The record type for the "filter" portion of the form
 */
const FilterSection = <K extends object>(props: FilterSectionProps<K>) => {
    const { filterCollections, handleFilterValueChange, onApplyFilter, onResetFilter, defaultFilterValues } = props
    const { watch, resetField } = useFormContext<SearchWithCriteriaFormType<K>>()

    const filterEntries = useMemo(
        () =>
            filterCollections.map((collection) => ({
                name: collection.name,
                allowMultiple: collection.allowMultiple,
                filterFields: collection.filterFields.flatMap(
                    (filterField) =>
                        Object.entries(filterField) as Entries<
                            Record<string, DiscriminatedFormFields & { name: keyof K }>
                        >
                ),
            })),
        [filterCollections]
    )

    const onFilterReset = () => {
        // "Remove Filters" clears every filter field the sidebar renders — not just the ones that
        // happen to have a configured default (an earlier version only reset the keys present in
        // defaultFilterValues, so e.g. status checkboxes stayed selected).
        filterEntries.forEach((group) =>
            group.filterFields.forEach((field) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                resetField(`filter.${field[1].name}`)
            })
        )
        // Then restore any configured defaults on top of the cleared fields.
        if (defaultFilterValues) {
            Object.entries(defaultFilterValues).forEach(([key, value]) => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                resetField(`filter.${key}`, { defaultValue: value })
            })
        }
        onResetFilter && onResetFilter()
    }

    // When the FilterSection changes, it receives the *full* form, not just the filter.  This works around that limitation
    const onFilterChange = (filter: K) => {
        handleFilterValueChange && handleFilterValueChange(filter as any)
    }

    // Update Inputs When They Change
    useEffect(() => {
        const subscription = watch((value) => {
            onFilterChange(value.filter as K)
        })
        return () => subscription.unsubscribe()
    }, [watch])

    return (
        <>
            <div className="filter-section">
                <div className={clsx('filter-options')}>
                    <h3>Filter</h3>
                    <div className="filter-fields-section">
                        {filterEntries.map((filterEntry) => (
                            <ExpandableItem
                                key={filterEntry.name as string}
                                heading={filterEntry.name}
                                className={clsx(!filterEntry.allowMultiple && 'multi-filter')}
                                persistExpandedState={false}
                            >
                                {filterEntry.filterFields.map((filterCollectionEntry) => (
                                    <FormFieldFromProps
                                        {...filterCollectionEntry[1]}
                                        name={`filter.${filterCollectionEntry[1].name}`}
                                        key={filterCollectionEntry[0] as string}
                                    />
                                ))}
                            </ExpandableItem>
                        ))}
                    </div>
                </div>
            </div>
            <div className="button-container">
                <Button mainColor type="secondary" onClick={onFilterReset} submitType="button">
                    Remove Filters
                </Button>
                <Button
                    mainColor
                    type="primary"
                    onClick={() => onApplyFilter && onApplyFilter(false)}
                    submitType="button"
                >
                    Apply Filters
                </Button>
            </div>
        </>
    )
}
export default FilterSection
